import React from "react";
import { motion } from "framer-motion";
import { Brain, TestTube2, User, ListChecks, FileText, Shield, Award, Plane } from "lucide-react";

const services = [
  {
    icon: Brain,
    title: "Career and Course Clarity",
    bullets: [
      "Clarity counselling — student and parent alignment",
      "Structured evaluation, including psychometric testing",
      "Course direction, pathway mapping, timeline planning",
    ],
    outcome: "Leave with a clear roadmap, not assumptions.",
    color: "#EEF0FC",
    iconColor: "#2B2F9E",
  },
  {
    icon: TestTube2,
    title: "Test Planning and Readiness",
    bullets: [
      "IELTS, TOEFL, PTE, Duolingo planning and prep strategy",
      "SAT/ACT for UG, GRE for Masters, GMAT for MBA",
      "Target score strategy, retake planning, booking guidance",
    ],
    outcome: "Right tests. Right timing. Right targets.",
    color: "#FFF7E6",
    iconColor: "#F5A000",
  },
  {
    icon: User,
    title: "Profile Building and Positioning",
    bullets: [
      "Profile audit and gap plan",
      "Activities, projects, and internships strategy",
      "CV structuring, achievement framing, LOR planning",
    ],
    outcome: "Build a profile that matches selective universities.",
    color: "#F0FDF4",
    iconColor: "#10B981",
  },
  {
    icon: ListChecks,
    title: "Shortlisting and Application Strategy",
    bullets: [
      "University shortlisting using fit, budget, and outcomes",
      "Safe, match, and ambitious programme mix",
      "Intake planning and application calendar",
    ],
    outcome: "Shortlists you can convert, not just admire.",
    color: "#FDF4FF",
    iconColor: "#9333EA",
  },
  {
    icon: FileText,
    title: "Applications, SOP, CV, and Reviews",
    bullets: [
      "SOP ideation, structure, writing guidance and review cycles",
      "Resume tailoring and application form guidance",
      "Portfolio guidance for design and creative pathways",
    ],
    outcome: "Applications that tell a coherent story.",
    color: "#EFF6FF",
    iconColor: "#3B82F6",
  },
  {
    icon: Shield,
    title: "Visa and Compliance",
    bullets: [
      "Documentation checklist and financial guidance",
      "Filing guidance, error prevention, interview preparation",
      "Risk checks and contingency planning",
    ],
    outcome: "Reduce stress. Avoid avoidable refusals.",
    color: "#FFF1F2",
    iconColor: "#EF4444",
  },
  {
    icon: Award,
    title: "Scholarships and Funding Strategy",
    bullets: [
      "Scholarship mapping and eligibility screening",
      "Funding plan inputs, total cost modelling",
      "ROI analysis built into every recommendation",
    ],
    outcome: "Scholarship and ROI thinking is built into every shortlist.",
    color: "#FEFCE8",
    iconColor: "#CA8A04",
  },
  {
    icon: Plane,
    title: "Pre-departure and Settling Support",
    bullets: [
      "Accommodation guidance, budgeting, insurance, packing",
      "Forex and ticketing support through partners (optional)",
      "Pre-arrival orientation and first-week readiness",
    ],
    outcome: "Arrive prepared. Start with confidence.",
    color: "#F0FDF4",
    iconColor: "#059669",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      style={{ padding: "100px 0", backgroundColor: "#fff" }}
    >
      <div style={{ maxWidth: "1280px", margin: "0 auto", padding: "0 24px" }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ marginBottom: "16px" }}
        >
          <span style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.12em", textTransform: "uppercase", color: "#F5A000", fontFamily: "'DM Sans', sans-serif" }}>
            What We Do
          </span>
          <h2 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: "clamp(26px, 2.5vw, 36px)",
            fontWeight: "700",
            color: "#0F172A",
            margin: "12px 0 16px",
            maxWidth: "540px",
            lineHeight: "1.25",
          }}>
            Services built around the student journey
          </h2>
          <p style={{ fontSize: "17px", color: "#64748B", fontFamily: "'DM Sans', sans-serif", maxWidth: "500px", lineHeight: "1.65" }}>
            Every service is designed to move you forward — methodically, and with senior oversight at every step.
          </p>
        </motion.div>

        {/* ROI callout */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "10px",
            backgroundColor: "#FEFCE8",
            border: "1px solid #FDE68A",
            borderRadius: "8px",
            padding: "10px 18px",
            marginBottom: "48px",
          }}
        >
          <Award size={16} style={{ color: "#CA8A04", flexShrink: 0 }} />
          <span style={{ fontSize: "14px", fontWeight: "600", color: "#92400E", fontFamily: "'DM Sans', sans-serif" }}>
            Scholarship and ROI thinking is built into our recommendations. Every shortlist considers total cost, funding options, and long-term return.
          </span>
        </motion.div>

        {/* Service cards — 4 columns, 2 rows */}
        <div className="mw-grid-4">
          {services.map((svc, i) => {
            const Icon = svc.icon;
            return (
              <motion.div
                key={svc.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                data-testid={`service-card-${i}`}
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "12px",
                  padding: "28px 24px",
                  border: "1px solid #E2E8F0",
                  display: "flex",
                  flexDirection: "column",
                  transition: "all 0.3s",
                  cursor: "default",
                }}
                whileHover={{ y: -4, boxShadow: "0 12px 32px rgba(0,0,0,0.08)", borderColor: "#CBD5E1" }}
              >
                <div style={{
                  width: "44px", height: "44px", borderRadius: "10px",
                  backgroundColor: svc.color,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  marginBottom: "14px", flexShrink: 0,
                }}>
                  <Icon size={20} style={{ color: svc.iconColor }} />
                </div>

                <h3 style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "18px", fontWeight: "700",
                  color: "#0F172A", margin: "0 0 14px", lineHeight: "1.3",
                }}>
                  {svc.title}
                </h3>

                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px", marginBottom: "16px" }}>
                  {svc.bullets.map((b) => (
                    <div key={b} style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                      <div style={{ width: "5px", height: "5px", borderRadius: "50%", backgroundColor: svc.iconColor, marginTop: "7px", flexShrink: 0 }} />
                      <span style={{ fontSize: "13px", color: "#64748B", fontFamily: "'DM Sans', sans-serif", lineHeight: "1.5" }}>{b}</span>
                    </div>
                  ))}
                </div>

                <div style={{
                  borderTop: "1px solid #F1F5F9", paddingTop: "14px",
                  fontSize: "13px", fontWeight: "600", color: svc.iconColor,
                  fontFamily: "'DM Sans', sans-serif", fontStyle: "italic",
                }}>
                  {svc.outcome}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Not a yes-man callout */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          style={{
            marginTop: "40px",
            padding: "28px 32px",
            backgroundColor: "#F8FAFC",
            borderRadius: "12px",
            borderLeft: "4px solid #2B2F9E",
          }}
        >
          <p style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "16px",
            fontWeight: "600",
            color: "#0F172A",
            margin: "0 0 8px",
          }}>
            We are comfortable telling you not to study abroad if the fit is not right.
          </p>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "15px", color: "#64748B", margin: 0, lineHeight: "1.6" }}>
            We work for you, even when the right answer is no. We sit on your side. No conflicts. No hidden incentives.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
